import { FC } from 'react';
import { Route } from 'react-router';
import { createUseStyles } from 'react-jss';
import { IonButton, IonButtons, IonContent, IonHeader, IonTitle, IonToolbar } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ArrowLeftIcon } from '../theme/Icons';
import { mediaMinWidth } from '../theme/utils';
import { Onboarding } from './Onboarding';

// The onboarding checklist used to be the home "Setup" tab. Now that the home page is tabless it lives
// here, reached from the hamburger nav. Home falls back here as the back target.
const HOME_PATH = '/caregiver';

export const OnboardingPageRouteHandler: FC = () => (
    <Route exact path="/setup" render={() => <OnboardingPage />} />
);

// Lines the column up with the pages that use InnerhivePage's own IonContent (Care, Community). Opting
// out of that IonContent also opts out of its margins, so this page restates them. Padding rather than an
// inset box because ion-content ships `margin: 0 !important`, and the header has to repeat it because it
// is slotted into the content rather than sitting in the scroll area the padding applies to. Viewport
// units, not percentages: skipAddingIonContent nests this page inside a wrapper already indented by the
// side nav, so a percentage would resolve against that narrower box and leave the column wider than
// every other page.
const columnPadding = {
    '--padding-start': 'var(--ion-padding, 16px)',
    '--padding-end': 'var(--ion-padding, 16px)',

    [mediaMinWidth.md]: {
        '--padding-start': '15vw',
        '--padding-end': '15vw',
    },

    [mediaMinWidth.lg]: {
        '--padding-start': '20vw',
        '--padding-end': '20vw',
    },
};

const useStyles = createUseStyles({
    content: {
        ...columnPadding,

        // skipAddingIonContent means IonContent isn't told the fixed IonHeader's height, so pad past it
        // (otherwise the first content -- the care-recipient switcher -- hides under the header)
        '--padding-top': 'calc(56px + var(--ion-safe-area-top)) !important',
        '--padding-bottom': '24px',
    },

    toolbar: columnPadding,
});

export const OnboardingPage: FC = () => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const history = useInstance(HistoryViewModel);

    const activeCareMapId = careMapState.getMostRecentCaremap()?.careMapId;

    return (
        <ActiveCareMapProviderV2 careMapId={activeCareMapId}>
            <InnerhivePage skipAddingIonContent hideAppHeader>
                <IonContent className={classes.content} data-type="onboarding-page">
                    <IonHeader slot="fixed">
                        <IonToolbar className={classes.toolbar}>
                            <IonButtons slot="start">
                                <IonButton data-type="back-button" fill="clear" shape="round" onClick={() => history.back({ fallbackPath: HOME_PATH })}>
                                    <ArrowLeftIcon color="dark" />
                                </IonButton>
                            </IonButtons>
                            <IonTitle>Set Up</IonTitle>
                        </IonToolbar>
                    </IonHeader>

                    <Onboarding />
                </IonContent>
            </InnerhivePage>
        </ActiveCareMapProviderV2>
    );
};
